<template lang="pug">
.segment-edit.mb-S
  div(v-if="!isLoading && classification")
    .top-line
      h1.h1 {{ forNew ? 'New' : 'Edit' }} visitor segment
      .save-cancel
        BriqButton(variant="text" wide @click="$router.push({name: 'VisitorClassificationsOverview'})" :disabled="isSaving") Cancel
        BriqSubmitButton(
          data-test-id="submit-button"
          @click="validateAndSave"
          :pending="isSaving"
          ) Save
    .panel
      BriqMultilanguageWarningModal(
        v-if="hasMultipleLanguages"
        v-show="showWarningModal"
        :defaultModel="fields"
        :translationInfo="fields.TranslationInfo"
        :translatableProperties="translatableProperties"
        @close="showWarningModal = false" 
        @onSubmit="save"
        )
      .edit-segment-header
        BriqButton.translation-button(
          v-if="hasMultipleLanguages"
          small
          variant="secondary"
          @click="toggleTranslation"
          data-test-id="translation-button"
          ) 
            .flex
              .translation-icon.mr-XXS
                BriqIcon(:name="`icon-input-${toggleTranslationIcon}-icon`") 
              .translation-text {{ toggleTranslationText }}
      BriqMultilanguage(
        v-if="enableMultilanguage && !forNew"
        v-model="activeCulture"
        :translationFields="translationFields"
        :cultures="availableCultures"
        :showSecondColumn="shouldShowMultilanguage"
        :defaultCulture="defaultCultureDisplayName"
        :nodesToHide="nodesToHide"
        )
        SegmentEditFields(
          :fields="fields"
          :colorOptions="colorOptions"
          :isSaving="isSaving"
          :canCreateSegments="canCreateSegments"
          :disableError="disableError"
          )
        template(v-slot:secondary)
          SegmentEditFields(
            v-if="activeCulture"
            :fields="translationModel"
            :colorOptions="colorOptions"
            :isSaving="isSaving"
            :canCreateSegments="canCreateSegments"
            :disableError="disableError"
            )
      SegmentEditFields(
        v-else
        :fields="fields"
        :colorOptions="colorOptions"
        :isSaving="isSaving"
        :canCreateSegments="canCreateSegments"
        :disableError="disableError"
        )
    .save-cancel.bottom-buttons
      BriqButton(variant="text" wide @click="$router.push({name: 'VisitorClassificationsOverview'})" :disabled="isSaving") Cancel
      BriqSubmitButton(
        data-test-id="submit-button-2"
        @click="validateAndSave"
        :pending="isSaving"
        ) Save
  Spinner(v-if="isLoading")
  div(v-if="!isLoading && !classification")
    span There are no classifications
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapActions } from 'vuex'
import ColorIndicator from '@/vue/settings/application/visitorclassifications/ColorIndicator.vue'
import VisitorClassificationService from '@/vue/settings/application/visitorclassifications/VisitorClassificationService.js'
import Spinner from '@v/components/Spinner.vue'
import SegmentEditFields from '@/vue/settings/application/visitorclassifications/SegmentEditFields.vue'
import BriqMultilanguage from '@v/components/BriqMultilanguage.vue'
import completeTranslationInfo from '@v/util/completeTranslationInfo.js'
import BriqMultilanguageWarningModal from '@/vue/components/BriqMultilanguageWarningModal.vue'
import { emptyTranslatableFieldsExist } from '@v/util/emptyTranslatableFields.js'

export default {
  components: {
    Multiselect,
    ColorIndicator,
    Spinner,
    SegmentEditFields,
    BriqMultilanguage,
    BriqMultilanguageWarningModal,
  },
  props: {
    canCreateSegments: Boolean,
    enableMultilanguage: Boolean,
  },
  data() {
    return {
      fields: {
        Name: '',
        NamePlural: '',
        Description: '',
        colorObject: '',
        enabled: false,
        TranslationInfo: {},
      },
      classification: null,
      forNew: false,
      code: null,
      isLoading: false,
      isSaving: false,
      order: null,
      isUsedByResources: false,
      activeCulture: null,
      showTranslations: false,
      showWarningModal: false,
    }
  },
  computed: {
    colorOptions() {
      return [
        { name: 'Sky Blue', color: '#009dff' },
        { name: 'Deep Sky Blue', color: '#00bbff' },
        { name: 'Turquoise', color: '#40e0d0' },
        { name: 'Green', color: 'green' },
        { name: 'Yellow', color: '#f8c83f' },
        { name: 'Gray', color: '#7F7F7F' },
      ]
    },
    disableError() {
      if (this.isUsedByResources && !this.fields.enabled)
        return 'This segment is used by resources and therefore cannot be disabled.'
      return null
    },
    toggleTranslationText() {
      const what = this.showTranslations ? 'Hide' : 'Show'
      return what + ' translations'
    },
    toggleTranslationIcon() {
      return this.showTranslations ? 'eye-crossed' : 'eye-open'
    },
    hasMultipleLanguages() {
      return this.enableMultilanguage && !!this.fields.TranslationInfo?.AvailableCultures?.length
    },
    shouldShowMultilanguage() {
      return this.hasMultipleLanguages && this.showTranslations
    },
    translationFields() {
      // todo: refactor to make it work with model field name instead of label
      return ['Name - single', 'Name - plural', 'Description']
    },
    translatableProperties() {
      return [
        {
          label: 'Name - single',
          key: 'Name',
        },
        {
          label: 'Name - plural',
          key: 'NamePlural',
        },
        {
          label: 'Description',
          key: 'Description',
        },
      ]
    },
    nodesToHide() {
      return ['.segment-edit-fields-color-picker']
    },
    defaultCulture() {
      return this.fields.TranslationInfo?.DefaultCulture
    },
    availableCultures() {
      return this.fields.TranslationInfo?.AvailableCultures.filter(
        c => c.Name !== this.defaultCulture.Name,
      )
    },
    defaultCultureDisplayName() {
      return this.defaultCulture ? this.defaultCulture.DisplayName : ''
    },
    translationModel() {
      if (!this.activeCulture) return null
      const key = this.activeCulture.Name
      return this.fields.TranslationInfo.Translations[key]
    },
  },
  methods: {
    ...mapActions('visitorClassifications', [
      'fetchSegment',
      'maybeFetchClassification',
      'maybeUpdateClassification',
    ]),
    validateAndSave() {
      if (
        this.hasMultipleLanguages &&
        emptyTranslatableFieldsExist(
          this.fields,
          this.fields.TranslationInfo,
          this.translatableProperties,
        )
      ) {
        this.showWarningModal = true
      } else {
        this.save()
      }
    },
    save() {
      const listofSegmentNames = this.classification.segments.map(item => item.name)
      if (!this.code && listofSegmentNames.includes(this.fields.Name)) {
        this.$notifyError('The segment code is already in use.')
        return
      }
      if (this.isUsedByResources && !this.fields.enabled) {
        this.$notifyError('This segment is used by resources and therefore cannot be disabled.')
        return
      }
      if (this.isSaving) return
      const model = this.createSaveModel()
      this.isSaving = true
      this.createOrUpdate(model)
        .then(() => {
          this.isSaving = false
          this.maybeUpdateClassification(this.classification.code)
          this.$router.push({ name: 'VisitorClassificationsOverview' })
          this.$notifySuccess('Classification saved')
        })
        .catch(error => {
          this.isSaving = false
          this.$logError('There was an error: ', error.response)
          this.$notifyError('Classification save failed!')
        })
    },
    createSaveModel() {
      return {
        code: this.code,
        name: this.fields.Name,
        namePlural: this.fields.NamePlural,
        description: this.fields.Description,
        color: this.fields.colorObject ? this.fields.colorObject.color : null,
        enabled: this.fields.enabled,
        order: this.forNew ? this.classification.segments.length + 1 : this.order,
        TranslationInfo: this.fields.TranslationInfo,
      }
    },
    createOrUpdate(model) {
      if (this.forNew) {
        return VisitorClassificationService.createSegment(this.classification.code, model)
      } else {
        return VisitorClassificationService.updateSegment(this.classification.code, model)
      }
    },
    toggleTranslation() {
      this.showTranslations = !this.showTranslations
    },
  },
  created() {
    this.forNew = !this.$route.params.segmentCode
    if (this.$route.params.classificationCode) {
      this.isLoading = true
      this.maybeFetchClassification(this.$route.params.classificationCode)
        .then(classification => {
          this.classification = classification
          if (this.$route.params.segmentCode) {
            this.fetchSegment({
              classificationCode: this.$route.params.classificationCode,
              segmentCode: this.$route.params.segmentCode,
            })
              .then(segment => {
                this.code = segment.code
                this.fields.Name = segment.name
                this.fields.NamePlural = segment.namePlural
                this.fields.Description = segment.description
                this.fields.enabled = segment.enabled
                this.fields.colorObject = this.colorOptions.find(c => c.color === segment.color)
                this.order = segment.order
                this.isUsedByResources = segment.isUsedByResources
                this.fields.TranslationInfo = segment.TranslationInfo

                if (this.hasMultipleLanguages) {
                  this.activeCulture = this.availableCultures[0]
                  completeTranslationInfo(this.fields)
                }
              })
              .finally(() => (this.isLoading = false))
          } else {
            this.isLoading = false
          }
        })
        .catch(error => {
          this.isLoading = false
          this.$notifyError('Something went wrong during fetch segment')
          this.$logError(error)
        })
    } else {
      this.$logWarning('Could not determine this.$route.params.classificationCode')
      this.$notifyWarning('Could not determine classification')
    }
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="stylus">
.segment-edit .multiselect__select
  height 100%

  .briq-form-element
    margin-bottom 24px
</style>

<style scoped lang="stylus">
.segment-edit
  padding 4em
  width 66.7%
  margin 0 auto
  min-width 800px

.panel
  background white
  border-radius 3px
  box-shadow 0 1px 3px 0 rgba(0, 0, 0, .05)
  border solid 1px #ececec
  padding 44px 44px
  margin-bottom 48px

.bottom-buttons
  text-align center

.save-cancel
  button:first-child
    margin-right 1em

.edit-segment-header
  display flex
  flex-direction row-reverse
  margin-bottom 20px

.translation-button
  margin-bottom 20px
</style>
